import React, { useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Show, useUser } from "@clerk/react";
import {
  useGetMyTeacherProfile,
  useListMyTeacherCourses,
  useListCourses,
  useClaimCourse,
  useCreateMyCourse,
  getGetMyTeacherProfileQueryKey,
  getListMyTeacherCoursesQueryKey,
  getListCoursesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { Course } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { GraduationCap, Pencil, Users, MessageSquare, Plus, Sparkles, PlusCircle, CalendarDays } from "lucide-react";
import { basePath } from "@/lib/clerk";
import { TeacherCalendar } from "@/components/teacher-calendar";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function CreateCourseDialog() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [topic, setTopic] = useState<"python" | "vibecoding" | "ai">("python");
  const [format, setFormat] = useState<"live" | "async">("live");
  const [level, setLevel] = useState<"middle" | "high">("high");
  const [priceDollars, setPriceDollars] = useState("0");
  const [lessonCount, setLessonCount] = useState("8");
  const [startDate, setStartDate] = useState("");
  const [, navigate] = useLocation();
  const create = useCreateMyCourse();
  const qc = useQueryClient();
  const { toast } = useToast();

  const onTitleChange = (v: string) => {
    setTitle(v);
    if (!slugDirty) setSlug(slugify(v));
  };

  const reset = () => {
    setTitle("");
    setSlug("");
    setSlugDirty(false);
    setTagline("");
    setDescription("");
    setTopic("python");
    setFormat("live");
    setLevel("high");
    setPriceDollars("0");
    setLessonCount("8");
    setStartDate("");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const lc = Number(lessonCount);
    const price = Math.round(Number(priceDollars) * 100);
    if (!Number.isFinite(lc) || lc < 1) {
      toast({ title: "Lesson count must be at least 1", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast({ title: "Enter a valid price", variant: "destructive" });
      return;
    }
    let startDateIso: string | undefined;
    if (startDate.trim()) {
      const d = new Date(startDate);
      if (Number.isNaN(d.getTime())) {
        toast({ title: "Enter a valid start date", variant: "destructive" });
        return;
      }
      startDateIso = d.toISOString();
    }
    try {
      const created = await create.mutateAsync({
        data: {
          slug,
          title: title.trim(),
          tagline: tagline.trim(),
          description: description.trim(),
          topic,
          format,
          level,
          priceCents: price,
          lessonCount: lc,
          ...(startDateIso ? { startDate: startDateIso } : {}),
        },
      });
      qc.invalidateQueries({ queryKey: getListMyTeacherCoursesQueryKey() });
      qc.invalidateQueries({ queryKey: getListCoursesQueryKey() });
      toast({
        title: "Course created",
        description: `"${created.title}" is ready to edit.`,
      });
      setOpen(false);
      reset();
      navigate(`/teacher/courses/${created.slug}/edit`);
    } catch (err) {
      toast({
        title: "Couldn't create course",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const canSubmit =
    !!title.trim() &&
    !!tagline.trim() &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) &&
    !create.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="button-new-course">
          <PlusCircle className="w-4 h-4 mr-1.5" /> New course
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Create a new course</DialogTitle>
          <DialogDescription>
            Set up the basics — you can flesh out lessons, descriptions, and pricing
            on the edit page right after.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="c-title">Title</Label>
            <Input
              id="c-title"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="Python Foundations"
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="c-slug">URL slug</Label>
            <Input
              id="c-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugDirty(true);
              }}
              placeholder="python-foundations"
              required
              pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lowercase letters, numbers, and dashes. Becomes /courses/{slug || "your-slug"}.
            </p>
          </div>
          <div>
            <Label htmlFor="c-tagline">Tagline</Label>
            <Input
              id="c-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Build real Python projects from scratch in 8 weeks."
              required
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="c-topic">Topic</Label>
              <Select value={topic} onValueChange={(v) => setTopic(v as typeof topic)}>
                <SelectTrigger id="c-topic" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="python">Python</SelectItem>
                  <SelectItem value="vibecoding">Vibecoding</SelectItem>
                  <SelectItem value="ai">AI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="c-format">Format</Label>
              <Select
                value={format}
                onValueChange={(v) => {
                  const next = v as typeof format;
                  setFormat(next);
                  if (next === "async") setStartDate("");
                }}
              >
                <SelectTrigger id="c-format" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live cohort</SelectItem>
                  <SelectItem value="async">Self-paced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="c-level">Grade</Label>
              <Select value={level} onValueChange={(v) => setLevel(v as typeof level)}>
                <SelectTrigger id="c-level" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="middle">Middle school</SelectItem>
                  <SelectItem value="high">High school</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-lessons">
                {format === "live" ? "Sessions" : "Lessons"}
              </Label>
              <Input
                id="c-lessons"
                type="number"
                min={1}
                max={50}
                value={lessonCount}
                onChange={(e) => setLessonCount(e.target.value)}
                required
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Empty placeholders you can fill in later.
              </p>
            </div>
            <div>
              <Label htmlFor="c-price">Price (USD)</Label>
              <Input
                id="c-price"
                type="number"
                min={0}
                step="1"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
                required
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use 0 for free.
              </p>
            </div>
          </div>
          {format === "live" && (
            <div>
              <Label htmlFor="c-start">Start date (optional)</Label>
              <Input
                id="c-start"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1.5"
                data-testid="input-start-date"
              />
              <p className="text-xs text-muted-foreground mt-1">
                When the first live session begins. Leave blank to set later.
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="c-desc">Description (optional)</Label>
            <Textarea
              id="c-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What students will learn and build."
              className="mt-1.5"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} className="rounded-full">
              {create.isPending ? "Creating…" : "Create course"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SignedOut() {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
        <GraduationCap className="w-12 h-12 mx-auto mb-6 text-primary" aria-hidden="true" />
        <h1 className="font-display text-4xl font-bold mb-3">Teacher portal</h1>
        <p className="text-muted-foreground mb-8">
          Sign in with your teacher account to manage your courses and message students.
        </p>
        <Button asChild className="rounded-full">
          <Link href={`/sign-in?redirect_url=${encodeURIComponent(`${basePath}/teacher`)}`}>
            Sign in
          </Link>
        </Button>
      </div>
    </Layout>
  );
}

const TEACHER_REQUEST_EMAIL = "info@mainloopacademy.com";

function NotApprovedCard() {
  const { user } = useUser();
  const userEmail = user?.primaryEmailAddress?.emailAddress ?? "";
  const subject = encodeURIComponent("Teacher access request — MainLoop");
  const body = encodeURIComponent(
    `Hi MainLoop team,\n\nPlease grant teacher access to my account.\n\nEmail: ${userEmail}\n\nThanks!`,
  );

  return (
    <div className="bg-card border border-border rounded-2xl p-8 md:p-10 shadow-sm">
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
        // invite only
      </div>
      <h2 className="font-display text-3xl font-bold mb-2">Teacher accounts are invite-only</h2>
      <p className="text-muted-foreground mb-2">
        You're signed in as{" "}
        <span className="font-mono text-foreground">{userEmail || "(unknown)"}</span>, but this email
        hasn't been approved as a MainLoop instructor.
      </p>
      <p className="text-muted-foreground mb-6">
        Teacher access is added manually by the MainLoop team. Drop us a line and we'll get you set up
        — once approved, just refresh this page and your portal will appear.
      </p>
      <Button asChild className="rounded-full">
        <a href={`mailto:${TEACHER_REQUEST_EMAIL}?subject=${subject}&body=${body}`}>
          <Mail className="w-4 h-4 mr-2" /> Request teacher access
        </a>
      </Button>
    </div>
  );
}

function ClaimSection() {
  const { data: allCourses } = useListCourses();
  const { data: myCourses } = useListMyTeacherCourses();
  const claim = useClaimCourse();
  const qc = useQueryClient();
  const { toast } = useToast();

  const ownedSlugs = new Set((myCourses ?? []).map((c) => c.slug));
  const unclaimed = (allCourses ?? []).filter((c) => !ownedSlugs.has(c.slug));

  if (unclaimed.length === 0) return null;

  const onClaim = async (slug: string) => {
    try {
      await claim.mutateAsync({ slug });
      qc.invalidateQueries({ queryKey: getListMyTeacherCoursesQueryKey() });
      qc.invalidateQueries({ queryKey: getListCoursesQueryKey() });
      toast({ title: "Course added", description: "It's now in your portal." });
    } catch (err) {
      toast({
        title: "Couldn't add course",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-12">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-secondary" aria-hidden="true" />
        <h3 className="font-display text-xl font-bold">Other courses</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Courses you don't teach yet. Add one to co-teach it alongside any other assigned teachers.
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {unclaimed.map((c) => (
          <li
            key={c.slug}
            className="flex items-center justify-between gap-3 border border-border rounded-xl px-4 py-3 bg-background"
          >
            <div className="min-w-0">
              <div className="font-medium truncate">{c.title}</div>
              <div className="text-xs text-muted-foreground truncate">{c.tagline}</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full shrink-0"
              onClick={() => onClaim(c.slug)}
              disabled={claim.isPending}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CourseCard({ c }: { c: Course }) {
  return (
    <li className="border border-border rounded-2xl p-5 bg-card hover:border-primary/40 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">
              {c.format}
            </Badge>
            <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider">
              {c.topic}
            </Badge>
            {c.status === "draft" && (
              <Badge
                variant="outline"
                className="rounded-full text-[10px] uppercase tracking-wider border-amber-500/50 text-amber-700 bg-amber-50"
                data-testid={`badge-draft-${c.slug}`}
              >
                Draft
              </Badge>
            )}
            {c.status === "unpublished" && (
              <Badge
                variant="outline"
                className="rounded-full text-[10px] uppercase tracking-wider border-muted-foreground/40 text-muted-foreground"
                data-testid={`badge-unpublished-${c.slug}`}
              >
                Unpublished
              </Badge>
            )}
          </div>
          <h3 className="font-display text-xl font-bold leading-tight">{c.title}</h3>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.tagline}</p>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <Link href={`/teacher/courses/${c.slug}/edit`}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Link>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <Link href={`/teacher/courses/${c.slug}/students`}>
            <Users className="w-3.5 h-3.5 mr-1.5" /> Students
          </Link>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <Link href={`/teacher/courses/${c.slug}/students#message`}>
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Message
          </Link>
        </Button>
      </div>
    </li>
  );
}

function TeacherHome() {
  const { data: profile } = useGetMyTeacherProfile();
  const { data: courses, isLoading } = useListMyTeacherCourses();

  if (!profile) return null;

  const all = courses ?? [];
  const active = all.filter((c) => c.status !== "unpublished");
  const unpublished = all.filter((c) => c.status === "unpublished");

  return (
    <div className="container mx-auto px-4 py-12 md:py-16 max-w-5xl">
      <div className="mb-10 pb-6 border-b border-border flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
            // teacher portal
          </div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Hi, {profile.name}
          </div>
          {profile.bio && (
            <p className="text-muted-foreground mt-2 max-w-xl">{profile.bio}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="rounded-full">
            {all.length} {all.length === 1 ? "course" : "courses"}
          </Badge>
          <CreateCourseDialog />
        </div>
      </div>

      {isLoading && (
        <div className="py-16 text-center text-muted-foreground" role="status">
          Loading your courses…
        </div>
      )}

      {!isLoading && all.length === 0 && (
        <>
          <div className="bg-muted/40 rounded-2xl p-12 text-center border border-dashed border-border">
            <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-60" aria-hidden="true" />
            <h3 className="font-display text-2xl font-bold mb-2">No courses yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Spin up a brand-new course or claim an existing one below to start
              editing it and messaging your students.
            </p>
            <CreateCourseDialog />
          </div>
        </>
      )}

      {!isLoading && all.length > 0 && (
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="active" data-testid="tab-active-courses">
              Active
              <Badge variant="secondary" className="ml-2 rounded-full text-[10px]">
                {active.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="unpublished" data-testid="tab-unpublished-courses">
              Unpublished
              <Badge variant="secondary" className="ml-2 rounded-full text-[10px]">
                {unpublished.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Calendar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {active.length === 0 ? (
              <div className="bg-muted/40 rounded-2xl p-10 text-center border border-dashed border-border text-muted-foreground">
                No active courses. Check the Unpublished tab or create a new course.
              </div>
            ) : (
              <ul className="grid gap-4 md:grid-cols-2">
                {active.map((c) => (
                  <CourseCard key={c.slug} c={c} />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="unpublished">
            {unpublished.length === 0 ? (
              <div className="bg-muted/40 rounded-2xl p-10 text-center border border-dashed border-border text-muted-foreground">
                Nothing here. Courses you unpublish will show up in this tab — students won't see them, but you can still edit and republish anytime.
              </div>
            ) : (
              <ul className="grid gap-4 md:grid-cols-2">
                {unpublished.map((c) => (
                  <CourseCard key={c.slug} c={c} />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="calendar">
            <TeacherCalendar />
          </TabsContent>
        </Tabs>
      )}

      <ClaimSection />
    </div>
  );
}

function SignedInTeacher() {
  const { user, isLoaded } = useUser();
  const profileQuery = useGetMyTeacherProfile({
    query: { retry: false, queryKey: getGetMyTeacherProfileQueryKey() },
  });

  if (!isLoaded || profileQuery.isLoading) {
    return (
      <div className="container mx-auto px-4 py-24 text-center text-muted-foreground" role="status">
        Loading teacher portal…
      </div>
    );
  }

  if (profileQuery.isError || !profileQuery.data) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-16 max-w-3xl">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          // teacher portal
        </div>
        <h1 className="font-display text-4xl font-bold mb-8">Welcome</h1>
        <NotApprovedCard />
      </div>
    );
  }

  return <TeacherHome />;
}

export default function TeacherDashboard() {
  return (
    <Layout>
      <Show when="signed-out">
        <SignedOut />
      </Show>
      <Show when="signed-in">
        <SignedInTeacher />
      </Show>
    </Layout>
  );
}

// Suppress unused — Redirect kept for future signed-in route guards
void Redirect;
